import { useEffect, useMemo, useState } from 'react'

const WHEEL_COLORS = ['#ffedd5', '#fed7aa', '#fdba74', '#fb923c', '#fdba74', '#fed7aa']

function uniqueNames(items) {
  const seen = new Set()
  const output = []
  items.forEach((item) => {
    const name = String(item || '').trim()
    const key = name.toLowerCase()
    if (!name || seen.has(key)) return
    seen.add(key)
    output.push(name)
  })
  return output
}

export default function Spinner({ currentUser }) {
  const [nameInput, setNameInput] = useState('')
  const [names, setNames] = useState(() => uniqueNames([currentUser?.name || 'Me']))
  const [winner, setWinner] = useState('')
  const [spinning, setSpinning] = useState(false)
  const [rotation, setRotation] = useState(0)

  useEffect(() => {
    const me = (currentUser?.name || '').trim()
    if (!me) return
    setNames((prev) => uniqueNames([me, ...prev]))
  }, [currentUser?.name])

  const helperText = useMemo(() => {
    if (names.length < 2) return 'Add at least 2 names to spin.'
    return `${names.length} participants ready.`
  }, [names.length])

  const segmentAngle = names.length ? 360 / names.length : 360

  // Gradient starting at 0deg (top / 12 o'clock position)
  const gradient = useMemo(() => {
    if (!names.length) return '#ffedd5'
    const parts = names.map((_, idx) => {
      const start = idx * segmentAngle
      const end = (idx + 1) * segmentAngle
      const color = WHEEL_COLORS[idx % WHEEL_COLORS.length]
      return `${color} ${start}deg ${end}deg`
    })
    return `conic-gradient(from 0deg, ${parts.join(', ')})`
  }, [names, segmentAngle])

  function addName() {
    if (!nameInput.trim()) return
    setNames((prev) => uniqueNames([...prev, nameInput]))
    setNameInput('')
  }

  function removeName(index) {
    setNames((prev) => prev.filter((_, i) => i !== index))
  }

  function spin() {
    if (spinning || names.length < 2) return
    const targetIndex = Math.floor(Math.random() * names.length)
    const turns = 5 + Math.floor(Math.random() * 3)
    
    // Calculate mid-angle of target segment so arrow points directly to center of slice
    const midSegmentAngle = targetIndex * segmentAngle + segmentAngle / 2
    
    // Degrees needed to bring target segment to top pointer (12 o'clock)
    const angleToTop = (360 - midSegmentAngle) % 360
    
    // Maintain continuous forward rotation
    const currentModulo = rotation % 360
    const extraTurns = turns * 360
    const nextRotation = rotation + extraTurns + ((angleToTop - currentModulo + 360) % 360)

    setSpinning(true)
    setWinner('')
    setRotation(nextRotation)

    window.setTimeout(() => {
      setWinner(names[targetIndex])
      setSpinning(false)
    }, 1200)
  }

  return (
    <section className="max-w-3xl mx-auto">
      <div className="fade-up glass-card rounded-3xl border border-white/60 p-6 shadow-xl">
        <h2 className="title-font text-2xl font-bold text-gray-900">Spinner 🎡</h2>
        <p className="mt-1 text-sm text-gray-600">Add your CoEater names and spin to pick one name.</p>

        {/* Input area */}
        <form
          onSubmit={(e) => {
            e.preventDefault()
            addName()
          }}
          className="mt-5 flex gap-2"
        >
          <input
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            placeholder="Enter a CoEater name"
            className="w-full rounded-xl border border-orange-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
          />
          <button
            type="submit"
            className="rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition"
          >
            Add
          </button>
        </form>

        {/* Name Tags */}
        <div className="mt-4 flex flex-wrap gap-2">
          {names.map((name, index) => (
            <span key={`${name}-${index}`} className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-800">
              {name}
              {index !== 0 && (
                <button type="button" onClick={() => removeName(index)} className="text-blue-700 hover:text-blue-900">✕</button>
              )}
            </span>
          ))}
        </div>

        {/* Wheel Area */}
        <div className="mt-6 flex flex-col items-center">
          <div className="relative flex flex-col items-center w-[270px]">
            {/* Pointer (Centered precisely relative to the 270px wheel) */}
            <div className="z-10 w-0 h-0 border-l-[14px] border-r-[14px] border-t-[20px] border-l-transparent border-r-transparent border-t-orange-600 -mb-2" />
            
            {/* Wheel Container */}
            <div
              className="relative h-[270px] w-[270px] rounded-full border-4 border-orange-200 shadow-lg transition-transform duration-[1200ms] ease-out overflow-hidden"
              style={{
                transform: `rotate(${rotation}deg)`,
                background: gradient,
              }}
            >
              {names.map((name, idx) => {
                const mid = idx * segmentAngle + segmentAngle / 2
                return (
                  <div
                    key={`${name}-${idx}`}
                    className="absolute left-1/2 top-1/2 text-[11px] font-extrabold text-orange-900 whitespace-nowrap"
                    style={{
                      transform: `translate(-50%, -50%) rotate(${mid}deg) translateY(-85px)`,
                      transformOrigin: 'center center',
                    }}
                  >
                    {name.length > 12 ? `${name.slice(0, 12)}...` : name}
                  </div>
                )
              })}
            </div>

            {/* Center Pin */}
            <div className="absolute top-[151px] left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-orange-700 border-2 border-white z-10" />
          </div>

          {/* Outcome Card */}
          <div className="mt-4 w-full rounded-2xl border border-orange-200 bg-orange-50 p-4 text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">Who will pay?</p>
            <p className="mt-2 text-sm text-gray-600">{helperText}</p>
            {winner && <p className="mt-3 text-2xl font-extrabold text-green-700">{winner}</p>}
          </div>
        </div>

        <button
          type="button"
          onClick={spin}
          disabled={spinning || names.length < 2}
          className="mt-5 w-full rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-4 py-3 text-sm font-semibold text-white hover:from-red-600 hover:to-orange-600 disabled:opacity-50 transition"
        >
          {spinning ? 'Spinning...' : 'Spin Now'}
        </button>
      </div>
    </section>
  )
}